import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from '../api';
import { PhoneCards, CardLine } from '../PhoneCards';
import { InboundRow, STATUS_TEXT, when } from './inboundApi';

// Inbounds (Fernando 21.–30.10.2026; docs/INBOUND.md): the supplier orders received at the warehouse. Most arrive by
// themselves from the supplier-order mails WGR copies to inbounds@ (backend/inbound_mail.py); season orders and Nora
// come from an uploaded quantity file here. Each inbound is then scanned into numbered boxes and replenished at its
// station (InboundDetail, InboundReplenish). "Ordered on" is WGR's own order date; every column sorts on click (the
// default is the WGR id, newest first; Waiting is the day's priority, one click away). -- Claude, 2026-10-29/30

const TABS = [{ key: 'open', label: 'Open' }, { key: 'done', label: 'Done' }, { key: 'archived', label: 'Archived' }];
const input = 'bg-[#e5e7eb] border border-[#9aa0a6] text-[#111111] rounded px-2 py-1.5 text-sm';
const STATUS_ORDER: Record<string, number> = { open: 0, scanning: 1, finished: 2, done: 3, archived: 4 };

type SortKey = 'wgr_order_id' | 'supplier_name' | 'waiting_pieces' | 'status' | 'products' | 'ordered' | 'scanned' | 'replenished' | 'ordered_on' | 'boxes' | 'booked_at';
type Sort = { key: SortKey; dir: 'asc' | 'desc' };
// the default is the WGR id, newest first (Fernando 30.10): a historical order the eye remembers; Waiting is one click away
const DEFAULT_SORT: Record<string, Sort> = { open: { key: 'wgr_order_id', dir: 'desc' }, done: { key: 'wgr_order_id', dir: 'desc' }, archived: { key: 'wgr_order_id', dir: 'desc' } };

function sortValue(r: InboundRow, key: SortKey): number | string {
  switch (key) {
    case 'wgr_order_id': return Number(r.wgr_order_id) || r.id;
    case 'supplier_name': return (r.supplier_name || '').toLowerCase();
    case 'waiting_pieces': return r.waiting_pieces || 0;
    case 'status': return STATUS_ORDER[r.status] ?? 9;
    case 'products': return r.products || 0;
    case 'ordered': return r.ordered || 0;
    case 'scanned': return r.scanned || 0;
    case 'replenished': return r.replenished || 0;
    case 'ordered_on': return r.ordered_on || '';
    case 'boxes': return r.boxes_expected ?? -1;
    case 'booked_at': return r.booked_at || '';
  }
}

export default function InboundsPage({ tab = 'open' }: { tab?: string }) {
  const navigate = useNavigate();
  const role = localStorage.getItem('wms_role') || 'staff';
  const manager = role === 'admin' || role === 'manager';
  const [rows, setRows] = useState<InboundRow[]>([]);
  const [err, setErr] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [sort, setSort] = useState<Sort>(() => {
    try { const v = localStorage.getItem(`inbounds.sort2.${tab}`); if (v) return JSON.parse(v); } catch { /* per-viewer convenience only */ }
    return DEFAULT_SORT[tab] || DEFAULT_SORT.open;
  });
  useEffect(() => {
    try { const v = localStorage.getItem(`inbounds.sort2.${tab}`); setSort(v ? JSON.parse(v) : (DEFAULT_SORT[tab] || DEFAULT_SORT.open)); } catch { setSort(DEFAULT_SORT[tab] || DEFAULT_SORT.open); }
  }, [tab]);

  const load = useCallback(async () => {
    setErr('');
    const r = await authFetch(`${API}/inbound?tab=${tab}`);
    const d = await r.json().catch(() => ({} as any));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setRows(d.inbounds || []);
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) => {
      const va = sortValue(a, sort.key), vb = sortValue(b, sort.key);
      const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
      if (c !== 0) return sort.dir === 'asc' ? c : -c;
      return (a.ordered_on || '').localeCompare(b.ordered_on || '');          // ties: the oldest order first
    });
    return out;
  }, [rows, sort]);

  const clickSort = (key: SortKey) => {
    const next: Sort = sort.key === key ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'supplier_name' || key === 'status' ? 'asc' : 'desc' };
    setSort(next);
    try { localStorage.setItem(`inbounds.sort2.${tab}`, JSON.stringify(next)); } catch { /* ignore */ }
  };
  const Th = ({ k, children, right, title }: { k: SortKey; children: React.ReactNode; right?: boolean; title?: string }) => (
    <th className={`py-2 pr-3 cursor-pointer select-none hover:text-[#111111] ${right ? 'text-right' : 'text-left'} ${sort.key === k ? 'text-[#111111]' : ''}`} onClick={() => clickSort(k)} title={title || 'click to sort'}>
      {children}{sort.key === k && <span className="ml-1 text-[10px]">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  return (
    <div className="min-h-screen bg-[#e5e7eb] text-[#111111]"><div className="p-4 sm:p-6 max-w-7xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Inbounds</h1>
          <p className="text-[#6b7280] text-sm mt-0.5">Supplier orders received at the warehouse — scanned into numbered boxes and put on the shelf with a cart tablet, finished by the worker, confirmed by a manager. Click a column to sort (newest WGR order first by default; Waiting shows the day's priority).</p>
        </div>
        {manager && (
          <button onClick={() => setShowNew(v => !v)} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg">
            {showNew ? 'Close' : '+ New inbound'}
          </button>
        )}
      </div>
      {showNew && <NewInbound onDone={id => { setShowNew(false); navigate(`/inbounds/${id}`); }} />}
      <div className="flex gap-1 mb-3 border-b border-[#d1d5db]">
        {TABS.map(t => (
          <button key={t.key} onClick={() => navigate(`/inbounds/${t.key}`)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === t.key ? 'border-blue-500 text-[#111111]' : 'border-transparent text-[#5f6368] hover:text-[#111111]'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {err && <div className="text-red-700 text-sm mb-3">{err}</div>}
      <PhoneCards rows={sorted} keyOf={r => r.id} empty={<div className="text-[#6b7280] text-sm">Nothing here.</div>}
        card={r => (
          <div onClick={() => navigate(`/inbounds/${r.id}`)} className="cursor-pointer">
            <div className="flex justify-between"><b className="text-[#111111] text-lg">{r.wgr_order_id || `WMS ${r.id}`}{(r.waiting_pieces || 0) > 0 && <span className="ml-2 text-sky-800 text-sm">{r.waiting_orders} waiting</span>}</b><span className="text-[#5f6368]">{STATUS_TEXT[r.status]}</span></div>
            <CardLine label="Supplier">{r.supplier_name || '—'}</CardLine>
            <CardLine label="Ordered">{when(r.ordered_on).slice(0, 10)}</CardLine>
            <CardLine label="Products">{r.products} · {r.ordered} pcs · {r.scanned} scanned · {r.replenished} on the shelf</CardLine>
            <CardLine label="Cartons">{r.boxes_expected ?? '?'} expected{r.arrived_at ? ` · ${r.arrived_boxes} arrived ${when(r.arrived_at).slice(0, 10)}` : ' · not arrived'}</CardLine>
          </div>
        )}>
        <table className="w-full text-sm">
          <thead className="text-[#6b7280] text-xs uppercase tracking-wider">
            <tr>
              <Th k="wgr_order_id">WGR order</Th>
              <Th k="supplier_name">Supplier</Th>
              <Th k="waiting_pieces" title="orders in Waiting for NOS-product that wait for this inbound — WGR's 'Booked items'">Waiting</Th>
              <Th k="status">Status</Th>
              <Th k="products" right title="products = style + colour, all sizes together">Products</Th>
              <Th k="ordered" right title="pieces ordered">Pcs</Th>
              <Th k="scanned" right>Scanned</Th>
              <Th k="replenished" right>Shelved</Th>
              <Th k="ordered_on" title="the date WGR created the supplier order">Ordered on</Th>
              <Th k="boxes">Cartons</Th>
              <Th k="booked_at">Booked in WGR</Th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={11} className="py-6 text-[#6b7280]">Nothing here.</td></tr>}
            {sorted.map(r => (
              <tr key={r.id} onClick={() => navigate(`/inbounds/${r.id}`)} className="border-t border-[#d1d5db] hover:bg-[#e0e3e8]/40 cursor-pointer">
                <td className="py-2 pr-3 text-[#111111] font-bold text-xl">{r.wgr_order_id || <span className="text-[#5f6368] text-base">WMS {r.id}</span>}{r.invoice && <span className="ml-2 text-base" title={`invoice attached: ${r.invoice}`}>📄</span>}
                  {r.source === 'wgr_mail' && <span className="ml-2 text-[10px] text-[#6b7280] uppercase font-normal align-middle">mail</span>}</td>
                <td className="pr-3 text-[#374151]">{r.supplier_name || '—'}</td>
                <td className="pr-3">{(r.waiting_pieces || 0) > 0
                  ? <span className="text-sky-800 font-semibold" title={`${r.waiting_orders} order(s), ${r.waiting_pieces} piece(s) waiting`}>{r.waiting_orders} <span className="text-sky-400/80 font-normal text-xs">orders · {r.waiting_pieces} pcs</span></span>
                  : <span className="text-[#8a9099]">—</span>}</td>
                <td className={`pr-3 ${r.status === 'finished' ? 'text-amber-800 font-semibold' : 'text-[#374151]'}`}>{STATUS_TEXT[r.status]}{r.status === 'finished' && r.finished_by ? ` (${r.finished_by})` : ''}{(r.short_lines || 0) > 0 && <span className="ml-2 text-red-700 text-xs">{r.short_lines} short</span>}</td>
                <td className="pr-3 text-right text-[#374151]">{r.products}</td>
                <td className="pr-3 text-right text-[#374151]">{r.ordered}</td>
                <td className="pr-3 text-right text-[#374151]">{r.scanned}</td>
                <td className="pr-3 text-right text-[#374151]">{r.replenished}</td>
                <td className="pr-3 text-[#374151]" title={r.wgr_created_at ? `WGR: ${when(r.wgr_created_at)} · in the WMS since ${when(r.created_at)}` : `no WGR date — in the WMS since ${when(r.created_at)}`}>{when(r.ordered_on).slice(0, 10)}</td>
                <td className="pr-3 text-[#374151]">
                  <span className="text-[#111111] font-semibold text-base">{r.boxes_expected ?? '?'}</span><span className="text-[#6b7280]"> expected</span>
                  {r.arrived_at ? <span className="text-[#5f6368]"> · {r.arrived_boxes} arrived {when(r.arrived_at).slice(0, 10)}</span> : <span className="text-amber-700/80"> · not arrived</span>}
                </td>
                <td className="pr-3 text-[#5f6368]">{r.booked_at ? `${when(r.booked_at)} · ${r.booked_by}` : (r.status === 'done' || r.status === 'archived') ? <span className="text-amber-700">not yet</span> : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PhoneCards>
    </div></div>
  );
}

// A quantity file → an inbound, in two steps: Preview (what the file holds, which articles the WMS knows, how many it
// would fetch from WGR) and Confirm. WGR's own xlsx (from a supplier-order mail) or the 2-column season CSV.
function NewInbound({ onDone }: { onDone: (id: number) => void }) {
  const [file, setFile] = useState<{ name: string; xlsx_b64?: string; csv_text?: string } | null>(null);
  const [wgrId, setWgrId] = useState('');
  const [supplier, setSupplier] = useState('');
  const [reference, setReference] = useState('');
  const [store, setStore] = useState('motley_denim');
  const [preview, setPreview] = useState<any>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const pick = (f: File | undefined) => {
    setPreview(null); setErr('');
    if (!f) { setFile(null); return; }
    const reader = new FileReader();
    if (/\.xlsx$/i.test(f.name)) {
      reader.onload = () => setFile({ name: f.name, xlsx_b64: String(reader.result).split(',')[1] || '' });
      reader.readAsDataURL(f);
    } else {
      reader.onload = () => setFile({ name: f.name, csv_text: String(reader.result) });
      reader.readAsText(f);
    }
  };
  const body = () => ({ filename: file?.name, xlsx_b64: file?.xlsx_b64, csv_text: file?.csv_text, wgr_order_id: wgrId.trim() || null,
    supplier_name: supplier.trim() || null, reference: reference.trim() || null, webshop_id: store });
  const run = async (step: 'preview' | 'confirm') => {
    if (!file) { setErr('Choose the file first'); return; }
    setBusy(true); setErr('');
    try {
      const r = await authFetch(`${API}/inbound/upload/${step}`, { method: 'POST', body: JSON.stringify(body()) });
      const d = await r.json().catch(() => ({} as any));
      if (!r.ok) { setErr(typeof d.detail === 'string' ? d.detail : `HTTP ${r.status}`); return; }
      if (step === 'preview') setPreview(d); else onDone(d.id);
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-[#d1d5db] p-5 mb-4">
      <div className="text-[#5f6368] text-xs uppercase tracking-widest font-semibold mb-3">New inbound from a file</div>
      <div className="flex flex-wrap gap-3 items-end">
        <label className="text-sm text-[#5f6368]">File (WGR's .xlsx, or a .csv with article number; quantity)
          <input type="file" accept=".xlsx,.csv,.txt" onChange={e => pick(e.target.files?.[0])} className="block mt-1 text-sm text-[#374151]" />
        </label>
        <label className="text-sm text-[#5f6368]">WGR order id
          <input value={wgrId} onChange={e => { setWgrId(e.target.value); setPreview(null); }} placeholder="11953" className={`${input} block mt-1 w-28`} />
        </label>
        <label className="text-sm text-[#5f6368]">Supplier
          <input value={supplier} onChange={e => setSupplier(e.target.value)} placeholder="Jack & Jones AW26 Season" className={`${input} block mt-1 w-64`} />
        </label>
        <label className="text-sm text-[#5f6368]">Reference / invoice
          <input value={reference} onChange={e => setReference(e.target.value)} className={`${input} block mt-1 w-40`} />
        </label>
        <label className="text-sm text-[#5f6368]">Store
          <select value={store} onChange={e => { setStore(e.target.value); setPreview(null); }} className={`${input} block mt-1`}>
            <option value="motley_denim">Motley Denim (WGR)</option>
            <option value="nora_mikken">Nora Mikken</option>
          </select>
        </label>
        <button disabled={busy || !file} onClick={() => run('preview')} className="px-4 py-2 bg-[#dfe3e8] hover:bg-[#c6cdd5] disabled:opacity-40 text-[#111111] text-sm rounded-lg">Preview</button>
      </div>
      {err && <div className="text-red-700 text-sm mt-3">{err}</div>}
      {preview && (
        <div className="mt-4 text-sm text-[#374151] space-y-1">
          <div><b className="text-[#111111]">{preview.order_id ? `WGR order ${preview.order_id}` : 'No WGR order id'}</b> · {preview.supplier || 'no supplier name'} · {preview.lines.length} lines · {preview.pieces} pieces{preview.created && <> · ordered {String(preview.created).slice(0, 10)}</>}</div>
          <div>{preview.known} articles known to the WMS{preview.unknown.length > 0 && <>, <span className="text-amber-700">{preview.unknown.length} not yet</span></>}.
            {preview.will_fetch > 0 && <> The import fetches <b className="text-[#111111]">{preview.will_fetch}</b> of them from WGR, one call each (cap {preview.cap}).</>}
            {preview.unknown.length > preview.will_fetch && <span className="text-red-700"> {preview.unknown.length - preview.will_fetch} stay without a product.</span>}
          </div>
          {preview.duplicate_of && <div className="text-red-700">WGR order {preview.order_id} is already inbound #{preview.duplicate_of.id} ({preview.duplicate_of.status}).</div>}
          {!preview.order_id && <div className="text-amber-700">Without a WGR order id the deltas cannot be booked against a supplier order — type it above for a Motley inbound.</div>}
          <div className="max-h-48 overflow-auto border border-[#d1d5db] rounded mt-2">
            <table className="w-full text-xs">
              <tbody>
                {preview.lines.map((l: any, i: number) => (
                  <tr key={i} className="border-t border-[#d1d5db]">
                    <td className="px-2 py-1 text-[#374151]">{l.article_number}</td><td className="px-2 text-[#5f6368]">{l.name || ''}</td>
                    <td className="px-2 text-[#5f6368]">{l.size || ''}</td><td className="px-2 text-right text-[#111111]">{l.qty_ordered}</td>
                    <td className="px-2 text-[#6b7280]">{l.product_ref ? '' : 'not in the WMS yet'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button disabled={busy || !!preview.duplicate_of} onClick={() => run('confirm')} className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-sm rounded-lg">
            Create the inbound{preview.will_fetch > 0 ? ` (${preview.will_fetch} WGR calls)` : ''}
          </button>
        </div>
      )}
    </div>
  );
}
